// src\pages\admin\ReadyMadeServices.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FiPackage,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiRefreshCw,
  FiDollarSign,
  FiClock,
  FiStar,
  FiCheckCircle,
  FiX,
  FiSave,
  FiImage,
  FiTag,
  FiGrid,
  FiList,
  FiEye,
  FiCopy,
  FiTrendingUp,
  FiUsers,
  FiHeart,
  FiLoader,
  FiUpload,
  FiFilter,
  FiChevronDown,
  FiChevronUp,
  FiInfo,
  FiCalendar,
  FiMessageCircle,
  FiAward,
  FiSettings,
  FiZoomIn,
  FiExternalLink,
  FiPenTool,
  FiCheck,
  FiSquare,
  FiMinus,
  FiDroplet,
} from 'react-icons/fi';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import { getCurrentUserToken } from '../../services/firebase';
import ServiceModal from '../../components/admin/ServiceModal';

// ============================================
// API BASE URL
// ============================================

const API_BASE = '/api/ready-made-services';

// ============================================
// MAIN COMPONENT
// ============================================

function ReadyMadeServices() {
  const { actualTheme } = useTheme();
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const isDark = actualTheme === 'dark';

  // ==================== STATE ====================

  // Data states
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // UI states
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Selection states
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [popularFilter, setPopularFilter] = useState('all');
  const [sortBy, setSortBy] = useState('order');
  const [sortOrder, setSortOrder] = useState('asc');

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    draft: 0,
    popular: 0,
    categories: {},
  });

  // Image upload states
  const [uploadingImage, setUploadingImage] = useState(false);

  // ==================== HELPERS ====================

  const getAuthToken = async () => {
    return await getCurrentUserToken();
  };

  // ==================== CATEGORY OPTIONS ====================

  const categoryOptions = [
    { value: 'branding', label: 'Branding', icon: FiPenTool },
    { value: 'product', label: 'Product Design', icon: FiPackage },
    { value: 'others', label: 'Others', icon: FiGrid },
  ];

  // ==================== API CALLS ====================

  const loadServices = useCallback(async () => {
    try {
      setLoading(true);

      // Get services
      const response = await fetch(`${API_BASE}?action=list`);
      const data = await response.json();

      if (data.success) {
        setServices(data.services || []);
      } else {
        showToast(data.message || 'Failed to load services', 'error');
      }

      // Get stats
      const statsResponse = await fetch(`${API_BASE}?action=stats`);
      const statsData = await statsResponse.json();
      if (statsData.success) {
        setStats(statsData.stats);
      }
    } catch (error) {
      console.error('Error loading services:', error);
      showToast('Failed to load services', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [showToast]);

  // ==================== FILTERS ====================

  const applyFilters = useCallback(() => {
    let filtered = [...services];

    if (statusFilter !== 'all') {
      filtered = filtered.filter(s => s.status === statusFilter);
    }

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(s => s.category === categoryFilter);
    }

    if (popularFilter !== 'all') {
      filtered = filtered.filter(
        s => s.popular === (popularFilter === 'popular'),
      );
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        s =>
          s.name?.toLowerCase().includes(term) ||
          s.description?.toLowerCase().includes(term) ||
          s.tags?.some(tag => tag.toLowerCase().includes(term)) ||
          s.category?.toLowerCase().includes(term),
      );
    }

    filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      if (sortBy === 'order') {
        aVal = a.order || 999;
        bVal = b.order || 999;
      } else if (sortBy === 'price') {
        aVal = parseFloat(a.price) || 0;
        bVal = parseFloat(b.price) || 0;
      } else if (sortBy === 'name') {
        aVal = a.name || '';
        bVal = b.name || '';
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredServices(filtered);
  }, [
    services,
    statusFilter,
    categoryFilter,
    popularFilter,
    searchTerm,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  // ==================== SELECTION HANDLERS ====================

  const toggleSelect = id => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id],
    );
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredServices.map(s => s.id));
    }
    setSelectAll(!selectAll);
  };

  const clearSelection = () => {
    setSelectedIds([]);
    setSelectAll(false);
  };

  // ==================== BULK DELETE ====================

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      showToast('No services selected', 'warning');
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedIds.length} selected service(s)? This action cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      const token = await getAuthToken();
      let deletedCount = 0;
      let failedCount = 0;

      for (const id of selectedIds) {
        try {
          const response = await fetch(`${API_BASE}?action=delete&id=${id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          });
          const data = await response.json();
          if (data.success) {
            deletedCount++;
          } else {
            failedCount++;
          }
        } catch (err) {
          failedCount++;
          console.error(`Failed to delete service ${id}:`, err);
        }
      }

      if (failedCount === 0) {
        showToast(`Successfully deleted ${deletedCount} service(s)`, 'success');
      } else {
        showToast(
          `Deleted ${deletedCount} service(s), ${failedCount} failed`,
          'warning',
        );
      }
      clearSelection();
      loadServices();
    } catch (error) {
      console.error('Error in bulk delete:', error);
      showToast('Failed to delete selected services', 'error');
    }
  };

  // ==================== CRUD OPERATIONS ====================

  const handleRefresh = () => {
    setRefreshing(true);
    loadServices();
  };

  const handleAddService = () => {
    setEditingService(null);
    setShowModal(true);
  };

  const handleEditService = service => {
    setEditingService(service);
    setShowModal(true);
  };

  const handleViewDetails = service => {
    setSelectedService(service);
    setShowDetailModal(true);
  };

  const handleDeleteService = async id => {
    if (
      !window.confirm(
        'Are you sure you want to delete this service? This action cannot be undone.',
      )
    ) {
      return;
    }

    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE}?action=delete&id=${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();
      if (data.success) {
        showToast('Service deleted successfully!', 'success');
        loadServices();
        if (showDetailModal) {
          setShowDetailModal(false);
          setSelectedService(null);
        }
        setSelectedIds(prev => prev.filter(sid => sid !== id));
      } else {
        showToast(data.message || 'Failed to delete service', 'error');
      }
    } catch (error) {
      console.error('Error deleting service:', error);
      showToast('Failed to delete service', 'error');
    }
  };

  const handleDuplicateService = async service => {
    try {
      const token = await getAuthToken();
      const formDataToSend = new FormData();

      // Copy all fields from the original service
      const fields = {
        name: `${service.name} (Copy)`,
        description: service.description,
        category: service.category,
        price: service.price,
        duration: service.duration,
        features: JSON.stringify(service.features || []),
        popular: 'false',
        icon: service.icon || 'FiPackage',
        order: services.length,
        status: 'draft',
        tags: JSON.stringify(service.tags || []),
        targetAudience: JSON.stringify(service.targetAudience || []),
        deliverables: JSON.stringify(service.deliverables || []),
        revisions: service.revisions || '',
        support: service.support || '',
        // SEO fields
        seoTitle: service.seoTitle || '',
        seoDescription: service.seoDescription || '',
        seoKeywords: JSON.stringify(service.seoKeywords || []),
        ogTitle: service.ogTitle || '',
        ogDescription: service.ogDescription || '',
        ogImage: service.ogImage || '',
        twitterTitle: service.twitterTitle || '',
        twitterDescription: service.twitterDescription || '',
        twitterImage: service.twitterImage || '',
      };

      Object.entries(fields).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          formDataToSend.append(key, value);
        }
      });

      const response = await fetch(`${API_BASE}?action=create`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formDataToSend,
      });

      const data = await response.json();
      if (data.success) {
        showToast('Service duplicated successfully!', 'success');
        loadServices();
      } else {
        showToast(data.message || 'Failed to duplicate service', 'error');
      }
    } catch (error) {
      console.error('Error duplicating service:', error);
      showToast('Failed to duplicate service', 'error');
    }
  };

  // ==================== IMAGE HANDLERS ====================

  const handleUploadImage = async (file, tempId) => {
    setUploadingImage(true);
    try {
      const token = await getAuthToken();
      const formData = new FormData();
      formData.append('image', file);
      formData.append('tempId', tempId);

      const response = await fetch(`${API_BASE}?action=upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await response.json();
      if (data.success) {
        return data.image;
      } else {
        throw new Error(data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteTempImage = async publicId => {
    try {
      const token = await getAuthToken();
      const response = await fetch(
        `${API_BASE}?action=delete-temp-image&publicId=${encodeURIComponent(publicId)}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const data = await response.json();
      if (data.success) {
        return true;
      } else {
        throw new Error(data.message || 'Delete failed');
      }
    } catch (error) {
      console.error('Delete temp image error:', error);
      throw error;
    }
  };

  const handleDeleteImage = async (serviceId, imageIndex) => {
    try {
      const token = await getAuthToken();
      const response = await fetch(
        `${API_BASE}?action=delete-image&id=${serviceId}&imageIndex=${imageIndex}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const data = await response.json();
      if (data.success) {
        return data;
      } else {
        throw new Error(data.message || 'Delete failed');
      }
    } catch (error) {
      console.error('Delete image error:', error);
      throw error;
    }
  };

  // ==================== SERVICE SUBMIT HANDLER ====================

  const handleServiceSubmit = async formData => {
    setSubmitting(true);
    try {
      const token = await getAuthToken();

      const url = editingService
        ? `${API_BASE}?action=update&id=${editingService.id}`
        : `${API_BASE}?action=create`;

      const method = editingService ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        showToast(
          editingService
            ? 'Service updated successfully!'
            : 'Service created successfully!',
          'success',
        );
        setShowModal(false);
        setEditingService(null);
        loadServices();
      } else {
        showToast(data.message || 'Failed to save service', 'error');
      }
    } catch (error) {
      console.error('Error saving service:', error);
      showToast('Failed to save service', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // ==================== FILTER RESET ====================

  const resetFilters = () => {
    setStatusFilter('all');
    setCategoryFilter('all');
    setPopularFilter('all');
    setSearchTerm('');
    setSortBy('order');
    setSortOrder('asc');
  };

  // ==================== STATUS BADGE ====================

  const getStatusBadge = status => {
    if (status === 'active') {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-200 dark:border-green-800/30">
          ● Active
        </span>
      );
    }
    return (
      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
        ○ Draft
      </span>
    );
  };

  // ==================== SERVICE CARD ====================

  const ServiceCard = ({ service }) => {
    const isSelected = selectedIds.includes(service.id);

    return (
      <div
        className={`group relative rounded-xl overflow-hidden border transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${
          isSelected ? 'ring-2 ring-black dark:ring-white' : ''
        }`}
        style={{
          backgroundColor: 'var(--bg)',
          borderColor: 'var(--border)',
        }}
      >
        {/* Selection Checkbox - Top Left */}
        <div className="absolute top-3 left-3 z-10">
          <button
            onClick={e => {
              e.stopPropagation();
              toggleSelect(service.id);
            }}
            className="w-5 h-5 rounded border-2 flex items-center justify-center transition-all hover:scale-110"
            style={{
              borderColor: isSelected ? 'var(--text)' : 'var(--border)',
              backgroundColor: isSelected ? 'var(--text)' : 'transparent',
            }}
          >
            {isSelected && (
              <FiCheck className="w-3 h-3" style={{ color: 'var(--bg)' }} />
            )}
          </button>
        </div>

        {/* Badges - Fixed positioning to prevent overlap */}
        <div className="absolute top-3 right-3 z-10 flex flex-col items-end gap-1.5">
          {service.popular && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 bg-black text-white dark:bg-white dark:text-black shadow-sm">
              <FiStar className="w-3 h-3" />
              Popular
            </span>
          )}
          {getStatusBadge(service.status)}
        </div>

        <div className="relative h-48 overflow-hidden bg-black/5 dark:bg-white/5">
          {service.imageUrl ? (
            <img
              src={service.imageUrl}
              alt={service.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <FiPackage
                className="w-16 h-16 opacity-30"
                style={{ color: 'var(--text)' }}
              />
            </div>
          )}

          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-2">
            <button
              onClick={() => handleEditService(service)}
              className="p-2 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm transition-all"
              title="Edit"
            >
              <FiEdit2 className="w-5 h-5 text-white" />
            </button>
            <button
              onClick={() => handleViewDetails(service)}
              className="p-2 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm transition-all"
              title="View Details"
            >
              <FiZoomIn className="w-5 h-5 text-white" />
            </button>
            <button
              onClick={() => handleDuplicateService(service)}
              className="p-2 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm transition-all"
              title="Duplicate"
            >
              <FiCopy className="w-5 h-5 text-white" />
            </button>
            <button
              onClick={() => handleDeleteService(service.id)}
              className="p-2 rounded-full bg-red-500/50 hover:bg-red-500/70 backdrop-blur-sm transition-all"
              title="Delete"
            >
              <FiTrash2 className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        <div className="p-4">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3
                className="font-bold text-lg line-clamp-1"
                style={{ color: 'var(--text)' }}
              >
                {service.name}
              </h3>
              <p
                className="text-xs flex items-center gap-1 mt-1"
                style={{ color: 'var(--muted)' }}
              >
                <FiTag className="w-3 h-3" />
                {categoryOptions.find(c => c.value === service.category)
                  ?.label || service.category}
              </p>
            </div>
            <div className="text-right">
              <p
                className="text-2xl font-bold"
                style={{ color: 'var(--text)' }}
              >
                ${service.price}
              </p>
              <p
                className="text-xs flex items-center gap-1"
                style={{ color: 'var(--muted)' }}
              >
                <FiClock className="w-3 h-3" />
                {service.duration}
              </p>
            </div>
          </div>

          <p
            className="text-sm line-clamp-2 mb-3"
            style={{ color: 'var(--muted)' }}
          >
            {service.description}
          </p>

          <div className="flex flex-wrap gap-1 mb-3">
            {service.features?.slice(0, 3).map((feature, idx) => (
              <span
                key={idx}
                className="text-xs px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: 'var(--border)',
                  color: 'var(--muted)',
                }}
              >
                {feature}
              </span>
            ))}
            {service.features?.length > 3 && (
              <span
                className="text-xs px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: 'var(--border)',
                  color: 'var(--muted)',
                }}
              >
                +{service.features.length - 3}
              </span>
            )}
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => handleEditService(service)}
              className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
              style={{
                border: '1px solid var(--border)',
                color: 'var(--text)',
              }}
            >
              <FiEdit2 className="w-3 h-3" />
              Edit
            </button>
            <button
              onClick={() => handleViewDetails(service)}
              className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
              style={{
                border: '1px solid var(--border)',
                color: 'var(--text)',
              }}
            >
              <FiEye className="w-3 h-3" />
              Details
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ==================== SERVICE LIST ITEM ====================

  const ServiceListItem = ({ service }) => {
    const isSelected = selectedIds.includes(service.id);

    return (
      <div
        className={`p-4 rounded-xl border transition-all hover:shadow-lg group ${
          isSelected ? 'ring-2 ring-black dark:ring-white' : ''
        }`}
        style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
      >
        <div className="flex items-center gap-4">
          {/* Checkbox */}
          <button
            onClick={() => toggleSelect(service.id)}
            className="w-5 h-5 rounded border-2 flex items-center justify-center transition-all hover:scale-110 flex-shrink-0"
            style={{
              borderColor: isSelected ? 'var(--text)' : 'var(--border)',
              backgroundColor: isSelected ? 'var(--text)' : 'transparent',
            }}
          >
            {isSelected && (
              <FiCheck className="w-3 h-3" style={{ color: 'var(--bg)' }} />
            )}
          </button>

          {/* Image */}
          <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-black/5 dark:bg-white/5">
            {service.imageUrl ? (
              <img
                src={service.imageUrl}
                alt={service.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <FiPackage
                  className="w-6 h-6 opacity-50"
                  style={{ color: 'var(--text)' }}
                />
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h3 className="font-semibold" style={{ color: 'var(--text)' }}>
                {service.name}
              </h3>
              {service.popular && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-black text-white dark:bg-white dark:text-black flex items-center gap-1">
                  <FiStar className="w-2 h-2" /> Popular
                </span>
              )}
              {getStatusBadge(service.status)}
            </div>
            <p
              className="text-sm line-clamp-1"
              style={{ color: 'var(--muted)' }}
            >
              {service.description}
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-2">
              <span
                className="text-xs flex items-center gap-1"
                style={{ color: 'var(--muted)' }}
              >
                <FiTag className="w-3 h-3" />
                {categoryOptions.find(c => c.value === service.category)?.label}
              </span>
              <span
                className="text-xs flex items-center gap-1"
                style={{ color: 'var(--muted)' }}
              >
                <FiDollarSign className="w-3 h-3" />${service.price}
              </span>
              <span
                className="text-xs flex items-center gap-1"
                style={{ color: 'var(--muted)' }}
              >
                <FiClock className="w-3 h-3" />
                {service.duration}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={() => handleEditService(service)}
              className="p-2 rounded-lg transition-all hover:bg-black/5 dark:hover:bg-white/5"
              title="Edit"
            >
              <FiEdit2 className="w-4 h-4" style={{ color: 'var(--muted)' }} />
            </button>
            <button
              onClick={() => handleViewDetails(service)}
              className="p-2 rounded-lg transition-all hover:bg-black/5 dark:hover:bg-white/5"
              title="View Details"
            >
              <FiEye className="w-4 h-4" style={{ color: 'var(--muted)' }} />
            </button>
            <button
              onClick={() => handleDuplicateService(service)}
              className="p-2 rounded-lg transition-all hover:bg-black/5 dark:hover:bg-white/5"
              title="Duplicate"
            >
              <FiCopy className="w-4 h-4" style={{ color: 'var(--muted)' }} />
            </button>
            <button
              onClick={() => handleDeleteService(service.id)}
              className="p-2 rounded-lg transition-all hover:bg-red-500/10"
              title="Delete"
            >
              <FiTrash2 className="w-4 h-4 text-red-500" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ==================== DETAIL MODAL ====================

  const DetailModal = () => {
    if (!selectedService) return null;

    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={() => setShowDetailModal(false)}
      >
        <div
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={() => setShowDetailModal(false)}
        />

        <div
          className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-xl animate-fade-in-up"
          style={{ backgroundColor: 'var(--bg)' }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="sticky top-0 z-10 p-6 border-b"
            style={{
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  {selectedService.popular && (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-black text-white dark:bg-white dark:text-black flex items-center gap-1">
                      <FiStar className="w-3 h-3" /> Most Popular
                    </span>
                  )}
                  {getStatusBadge(selectedService.status)}
                </div>
                <h2
                  className="text-2xl font-bold"
                  style={{ color: 'var(--text)' }}
                >
                  {selectedService.name}
                </h2>
                <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>
                  {selectedService.category}
                </p>
              </div>
              <div className="flex gap-2">
                <a
                  href={`/services`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full transition-all hover:bg-black/5 dark:hover:bg-white/5"
                  title="View on site"
                >
                  <FiExternalLink
                    className="w-5 h-5"
                    style={{ color: 'var(--text)' }}
                  />
                </a>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-all"
                >
                  <FiX className="w-5 h-5" style={{ color: 'var(--text)' }} />
                </button>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Image with 16:9 ratio */}
            {selectedService.imageUrl && (
              <div className="rounded-xl overflow-hidden bg-black/5 dark:bg-white/5">
                <div className="relative w-full pt-[56.25%]">
                  <img
                    src={selectedService.imageUrl}
                    alt={selectedService.name}
                    className="absolute top-0 left-0 w-full h-full object-cover"
                  />
                </div>
                <div
                  className="p-2 text-center border-t"
                  style={{ borderColor: 'var(--border)' }}
                >
                  <p className="text-xs" style={{ color: 'var(--muted)' }}>
                    🎬 Recommended image ratio: 16:9 (1920x1080px or 1280x720px)
                  </p>
                </div>
              </div>
            )}

            {/* Description */}
            <div>
              <h3
                className="text-lg font-semibold mb-2 flex items-center gap-2"
                style={{ color: 'var(--text)' }}
              >
                <FiMessageCircle className="w-4 h-4" />
                Description
              </h3>
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                {selectedService.description}
              </p>
            </div>

            {/* Price & Duration */}
            <div className="grid grid-cols-2 gap-4">
              <div
                className="p-4 rounded-xl"
                style={{ backgroundColor: 'var(--border)' }}
              >
                <p className="text-xs mb-1" style={{ color: 'var(--muted)' }}>
                  Starting Price
                </p>
                <p
                  className="text-3xl font-bold"
                  style={{ color: 'var(--text)' }}
                >
                  ${selectedService.price}
                </p>
              </div>
              <div
                className="p-4 rounded-xl"
                style={{ backgroundColor: 'var(--border)' }}
              >
                <p className="text-xs mb-1" style={{ color: 'var(--muted)' }}>
                  Delivery Time
                </p>
                <p
                  className="text-3xl font-bold"
                  style={{ color: 'var(--text)' }}
                >
                  {selectedService.duration}
                </p>
              </div>
            </div>

            {/* Features */}
            {selectedService.features?.length > 0 && (
              <div>
                <h3
                  className="text-lg font-semibold mb-3 flex items-center gap-2"
                  style={{ color: 'var(--text)' }}
                >
                  <FiCheckCircle className="w-4 h-4 text-green-500" />
                  Features & Inclusions
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {selectedService.features.map((feature, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 text-sm"
                      style={{ color: 'var(--muted)' }}
                    >
                      <FiCheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Deliverables */}
            {selectedService.deliverables?.length > 0 && (
              <div>
                <h3
                  className="text-lg font-semibold mb-3 flex items-center gap-2"
                  style={{ color: 'var(--text)' }}
                >
                  <FiPackage className="w-4 h-4" />
                  Deliverables
                </h3>
                <div className="flex flex-wrap gap-2">
                  {selectedService.deliverables.map((item, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 rounded-full text-sm"
                      style={{
                        backgroundColor: 'var(--border)',
                        color: 'var(--text)',
                      }}
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            {selectedService.tags?.length > 0 && (
              <div>
                <h3
                  className="text-lg font-semibold mb-3 flex items-center gap-2"
                  style={{ color: 'var(--text)' }}
                >
                  <FiTag className="w-4 h-4" />
                  Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {selectedService.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 rounded-full text-sm"
                      style={{
                        backgroundColor: 'var(--border)',
                        color: 'var(--muted)',
                      }}
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Target Audience */}
            {selectedService.targetAudience?.length > 0 && (
              <div>
                <h3
                  className="text-lg font-semibold mb-3 flex items-center gap-2"
                  style={{ color: 'var(--text)' }}
                >
                  <FiUsers className="w-4 h-4" />
                  Target Audience
                </h3>
                <div className="flex flex-wrap gap-2">
                  {selectedService.targetAudience.map((audience, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 rounded-full text-sm"
                      style={{
                        backgroundColor: 'var(--border)',
                        color: 'var(--text)',
                      }}
                    >
                      {audience}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Revisions & Support */}
            <div className="grid grid-cols-2 gap-4">
              {selectedService.revisions && (
                <div
                  className="p-3 rounded-lg"
                  style={{ backgroundColor: 'var(--border)' }}
                >
                  <p className="text-xs mb-1" style={{ color: 'var(--muted)' }}>
                    Revisions
                  </p>
                  <p className="font-medium" style={{ color: 'var(--text)' }}>
                    {selectedService.revisions}
                  </p>
                </div>
              )}
              {selectedService.support && (
                <div
                  className="p-3 rounded-lg"
                  style={{ backgroundColor: 'var(--border)' }}
                >
                  <p className="text-xs mb-1" style={{ color: 'var(--muted)' }}>
                    Support
                  </p>
                  <p className="font-medium" style={{ color: 'var(--text)' }}>
                    {selectedService.support}
                  </p>
                </div>
              )}
            </div>

            {/* SEO Info */}
            {(selectedService.seoTitle || selectedService.ogTitle) && (
              <div
                className="p-4 rounded-xl border"
                style={{ borderColor: 'var(--border)' }}
              >
                <h3
                  className="text-sm font-semibold mb-2 flex items-center gap-2"
                  style={{ color: 'var(--text)' }}
                >
                  <FiTrendingUp className="w-4 h-4 text-blue-500" />
                  SEO & Social Sharing
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                  {selectedService.seoTitle && (
                    <div>
                      <span style={{ color: 'var(--muted)' }}>SEO Title:</span>
                      <span className="ml-1" style={{ color: 'var(--text)' }}>
                        {selectedService.seoTitle}
                      </span>
                    </div>
                  )}
                  {selectedService.ogTitle && (
                    <div>
                      <span style={{ color: 'var(--muted)' }}>OG Title:</span>
                      <span className="ml-1" style={{ color: 'var(--text)' }}>
                        {selectedService.ogTitle}
                      </span>
                    </div>
                  )}
                  {selectedService.seoDescription && (
                    <div className="md:col-span-2">
                      <span style={{ color: 'var(--muted)' }}>
                        SEO Description:
                      </span>
                      <span className="ml-1" style={{ color: 'var(--text)' }}>
                        {selectedService.seoDescription}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Meta Info */}
            <div
              className="pt-4 border-t flex justify-between text-xs"
              style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}
            >
              <span>
                Created:{' '}
                {new Date(selectedService.createdAt).toLocaleDateString()}
              </span>
              <span>
                Last Updated:{' '}
                {new Date(selectedService.updatedAt).toLocaleDateString()}
              </span>
              <span>Order: {selectedService.order || 0}</span>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  handleEditService(selectedService);
                }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80"
                style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
              >
                <FiEdit2 className="w-4 h-4" />
                Edit Service
              </button>
              <button
                onClick={() => handleDuplicateService(selectedService)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80"
                style={{
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                <FiCopy className="w-4 h-4" />
                Duplicate
              </button>
              <button
                onClick={() => handleDeleteService(selectedService.id)}
                className="px-4 py-2 rounded-xl transition-all hover:opacity-80"
                style={{ backgroundColor: '#ef4444', color: 'white' }}
              >
                <FiTrash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ==================== RENDER ====================

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>
            Ready-made Services
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>
            Manage all ready-made service packages offered to clients
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {selectedIds.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80 bg-red-500 text-white"
            >
              <FiTrash2 className="w-4 h-4" />
              <span className="text-sm">
                Delete Selected ({selectedIds.length})
              </span>
            </button>
          )}
          <button
            onClick={handleAddService}
            className="flex items-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80"
            style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
          >
            <FiPlus className="w-4 h-4" />
            <span className="text-sm">Add Service</span>
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80"
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            <FiRefreshCw
              className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`}
            />
            <span className="text-sm">Refresh</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div
          className="p-4 rounded-xl border cursor-pointer hover:scale-105 transition-all"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
          onClick={() => {
            resetFilters();
            setStatusFilter('all');
          }}
        >
          <div className="text-2xl font-bold text-purple-500">
            {stats.total}
          </div>
          <div className="text-xs" style={{ color: 'var(--muted)' }}>
            Total Services
          </div>
        </div>
        <div
          className="p-4 rounded-xl border cursor-pointer hover:scale-105 transition-all"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
          onClick={() => {
            resetFilters();
            setStatusFilter('active');
          }}
        >
          <div className="text-2xl font-bold text-green-500">
            {stats.active}
          </div>
          <div className="text-xs" style={{ color: 'var(--muted)' }}>
            Active Services
          </div>
        </div>
        <div
          className="p-4 rounded-xl border cursor-pointer hover:scale-105 transition-all"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
          onClick={() => {
            resetFilters();
            setPopularFilter('popular');
          }}
        >
          <div className="text-2xl font-bold text-yellow-500">
            {stats.popular}
          </div>
          <div className="text-xs" style={{ color: 'var(--muted)' }}>
            Popular Services
          </div>
        </div>
        <div
          className="p-4 rounded-xl border cursor-pointer hover:scale-105 transition-all"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
          onClick={() => {
            resetFilters();
            setStatusFilter('draft');
          }}
        >
          <div className="text-2xl font-bold text-gray-500">{stats.draft}</div>
          <div className="text-xs" style={{ color: 'var(--muted)' }}>
            Draft Services
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <FiSearch
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
            style={{ color: 'var(--muted)' }}
          />
          <input
            type="text"
            placeholder="Search services..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded-xl outline-none transition-all"
            style={{
              backgroundColor: 'var(--bg)',
              border: '1px solid var(--border)',
              color: 'var(--text)',
            }}
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${showFilters ? 'bg-black/10 dark:bg-white/10' : ''}`}
            style={{ color: 'var(--text)' }}
          >
            <FiFilter className="w-4 h-4" />
            <span className="text-sm hidden sm:inline">Filters</span>
            <FiChevronDown
              className={`w-3 h-3 transition-transform ${showFilters ? 'rotate-180' : ''}`}
            />
          </button>
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-black/10 dark:bg-white/10' : ''}`}
            style={{ color: 'var(--text)' }}
          >
            <FiGrid className="w-5 h-5" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-black/10 dark:bg-white/10' : ''}`}
            style={{ color: 'var(--text)' }}
          >
            <FiList className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div
          className="p-4 rounded-xl border animate-fade-in"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label
                className="text-xs font-medium mb-1 block"
                style={{ color: 'var(--muted)' }}
              >
                Status
              </label>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                style={{
                  backgroundColor: 'var(--border)',
                  color: 'var(--text)',
                }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
              </select>
            </div>

            <div>
              <label
                className="text-xs font-medium mb-1 block"
                style={{ color: 'var(--muted)' }}
              >
                Category
              </label>
              <select
                value={categoryFilter}
                onChange={e => setCategoryFilter(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                style={{
                  backgroundColor: 'var(--border)',
                  color: 'var(--text)',
                }}
              >
                <option value="all">All Categories</option>
                {categoryOptions.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                className="text-xs font-medium mb-1 block"
                style={{ color: 'var(--muted)' }}
              >
                Popularity
              </label>
              <select
                value={popularFilter}
                onChange={e => setPopularFilter(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                style={{
                  backgroundColor: 'var(--border)',
                  color: 'var(--text)',
                }}
              >
                <option value="all">All</option>
                <option value="popular">Popular Only</option>
                <option value="normal">Normal Only</option>
              </select>
            </div>

            <div>
              <label
                className="text-xs font-medium mb-1 block"
                style={{ color: 'var(--muted)' }}
              >
                Sort By
              </label>
              <div className="flex gap-2">
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg text-sm outline-none"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  <option value="order">Order</option>
                  <option value="name">Name</option>
                  <option value="price">Price</option>
                </select>
                <button
                  onClick={() =>
                    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
                  }
                  className="px-3 py-2 rounded-lg transition-all"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  {sortOrder === 'asc' ? (
                    <FiChevronUp className="w-4 h-4" />
                  ) : (
                    <FiChevronDown className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center mt-4">
            <div className="text-xs" style={{ color: 'var(--muted)' }}>
              {selectedIds.length > 0 && `${selectedIds.length} selected`}
            </div>
            <div className="flex gap-2">
              {selectedIds.length > 0 && (
                <button
                  onClick={clearSelection}
                  className="px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
                  style={{
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  Clear Selection
                </button>
              )}
              <button
                onClick={resetFilters}
                className="px-4 py-1.5 rounded-lg text-sm transition-all hover:opacity-80"
                style={{
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Services Grid/List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div
            className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: 'var(--text)' }}
          />
        </div>
      ) : filteredServices.length === 0 ? (
        <div
          className="text-center py-12 rounded-2xl border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <FiPackage
            className="w-12 h-12 mx-auto mb-3 opacity-50"
            style={{ color: 'var(--muted)' }}
          />
          <p style={{ color: 'var(--muted)' }}>
            No services found matching your criteria
          </p>
          <button
            onClick={resetFilters}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm"
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            Clear Filters
          </button>
        </div>
      ) : (
        <>
          {/* Select All */}
          <div className="flex items-center gap-3 px-1">
            <button
              onClick={toggleSelectAll}
              className="flex items-center gap-2 text-sm transition-all hover:opacity-80"
              style={{ color: 'var(--muted)' }}
            >
              <span
                className="w-4 h-4 rounded border-2 flex items-center justify-center"
                style={{ borderColor: 'var(--border)' }}
              >
                {selectAll && (
                  <FiCheck
                    className="w-3 h-3"
                    style={{ color: 'var(--text)' }}
                  />
                )}
              </span>
              {selectAll ? 'Deselect All' : 'Select All'}
              <span className="text-xs" style={{ color: 'var(--muted)' }}>
                ({filteredServices.length} items)
              </span>
            </button>
            {selectedIds.length > 0 && (
              <span className="text-xs" style={{ color: 'var(--muted)' }}>
                • {selectedIds.length} selected
              </span>
            )}
          </div>

          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredServices.map(service => (
                <ServiceCard key={service.id} service={service} />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredServices.map(service => (
                <ServiceListItem key={service.id} service={service} />
              ))}
            </div>
          )}
        </>
      )}

      {/* Service Modal */}
      <ServiceModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingService(null);
        }}
        service={editingService}
        onSave={handleServiceSubmit}
        onUploadImage={handleUploadImage}
        onDeleteImage={handleDeleteImage}
        onDeleteTempImage={handleDeleteTempImage}
        isEditing={!!editingService}
        saving={submitting}
        serviceId={editingService?.id}
      />

      {/* Detail Modal */}
      {showDetailModal && <DetailModal />}
    </div>
  );
}

export default ReadyMadeServices;
